import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit, Trash2, Phone, Mail, MapPin, Horseshoe, HammerIcon } from "lucide-react";

import { useNotification } from "../contexts/NotificationContext";
import { getApiUrl } from "../config/api";

export default function Farriers() {
  const { showSuccess, showError, showWarning } = useNotification();
  const [farriers, setFarriers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    notes: ""
  });

  const [editData, setEditData] = useState({});

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingFarrier, setDeletingFarrier] = useState(null);

  useEffect(() => {
    fetchFarriers();
  }, []);

  const fetchFarriers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(getApiUrl("/api/farriers"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFarriers(res.data);
    } catch (error) {
      console.error("Error fetching farriers:", error);
      setError("Failed to load farriers");
      showError("Failed to load farriers");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.name.trim()) {
      showWarning("Farrier name is required");
      return;
    }

    try {
      await axios.post(getApiUrl("/api/farriers"), form, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setForm({
        name: "",
        phone: "",
        email: "",
        notes: ""
      });
      setShowModal(false);
      showSuccess("Farrier added successfully!");
      fetchFarriers();
    } catch (err) {
      console.error("Submit error:", err);
      if (err.response?.data?.error) {
        showError(err.response.data.error);
      } else {
        showError("Failed to add farrier");
      }
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm({
      name: "",
      phone: "",
      email: "",
      notes: ""
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({
      name: "",
      phone: "",
      email: "",
      notes: ""
    });
  };

  const startEdit = (farrier) => {
    setEditingId(farrier.id);
    setEditData({
      name: farrier.name || "",
      phone: farrier.phone || "",
      email: farrier.email || "",
      notes: farrier.notes || ""
    });
  };

  const saveEdit = async (id) => {
    // Validate required fields
    if (!editData.name?.trim()) {
      showWarning("Farrier name is required");
      return;
    }

    try {
      await axios.put(getApiUrl(`/api/farriers/${id}`), editData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setEditingId(null);
      showSuccess("Farrier updated successfully!");
      fetchFarriers();
    } catch (err) {
      console.error("Update error:", err);
      if (err.response?.data?.error) {
        showError(err.response.data.error);
      } else {
        showError("Failed to update farrier");
      }
    }
  };

  const cancelEdit = () => setEditingId(null);

  const handleDelete = async (id) => {
    setDeletingFarrier(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletingFarrier) return;
    try {
      await axios.delete(getApiUrl(`/api/farriers/${deletingFarrier}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      showSuccess("Farrier deleted successfully!");
      fetchFarriers();
    } catch (err) {
      console.error("Delete error:", err);
      showError("Failed to delete farrier");
    } finally {
      setDeletingFarrier(null);
      setShowDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setDeletingFarrier(null);
    setShowDeleteConfirm(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <HammerIcon className="mr-2 h-8 w-8 text-black" />
          Farriers
        </h2>
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-red-600">Error: {error}</p>
          <button 
            onClick={fetchFarriers}
            className="mt-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <HammerIcon className="mr-2 h-8 w-8 text-black" />
          Farriers
        </h1>
        <button
          onClick={openAddModal}
          className="bg-[#51380d] text-white px-4 py-2 rounded hover:bg-[#3d2a0a] flex items-center"
        >
          <Plus className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Add Farrier</span>
        </button>
      </div>

      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">Farrier List</h3>
        {farriers.length === 0 ? (
          <div className="text-center py-8">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No farriers yet</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first farrier</p>
            <button
              onClick={openAddModal}
              className="bg-[#51380d] text-white px-4 py-2 rounded hover:bg-[#3d2a0a]"
            >
              Add Your First Farrier
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {farriers.map((farrier) => (
              <div key={farrier.id} className="p-6">
                {editingId === farrier.id ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        className="border px-2 py-1 rounded w-full"
                        name="name"
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        placeholder="Farrier name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        className="border px-2 py-1 rounded w-full"
                        name="phone"
                        value={editData.phone}
                        onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        className="border px-2 py-1 rounded w-full"
                        type="email"
                        name="email"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        placeholder="Email address"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        className="border px-2 py-1 rounded w-full"
                        name="notes"
                        value={editData.notes}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        placeholder="Additional notes about the farrier"
                        rows="3"
                      />
                    </div>
                    <div className="sm:col-span-2 flex space-x-2">
                      <button
                        onClick={() => saveEdit(farrier.id)}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{farrier.name}</h3>
                      <div className="space-y-1 text-sm text-gray-600">
                        {farrier.phone && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-2" />
                            {farrier.phone}
                          </div>
                        )}
                        {farrier.email && (
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-2" />
                            {farrier.email}
                          </div>
                        )}
                        {farrier.notes && (
                          <div className="flex items-start">
                            <MapPin className="w-4 h-4 mr-2 mt-0.5" />
                            <span>{farrier.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => startEdit(farrier)}
                        className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                        title="Edit"
                      >
                        Edit
                      </button>
                        
                      <button
                        onClick={() => handleDelete(farrier.id)}
                        className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50 border border-red-200"
                        title="Delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add New Farrier
              </h3>
              
              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g., John Smith"
                    className="border px-3 py-2 rounded w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="e.g., (555) 123-4567"
                    className="border px-3 py-2 rounded w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="e.g., john@farrier.com"
                    className="border px-3 py-2 rounded w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="Any additional information"
                    className="border px-3 py-2 rounded w-full"
                    rows="3"
                  />
                </div>
                <div className="flex justify-center space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#51380d] text-white rounded-md hover:bg-[#3d2a0a] transition-colors"
                  >
                    Add Farrier
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-[70]">
          <div className="relative p-8 border w-96 shadow-lg rounded-md bg-white">
            <div className="text-center">
              <Trash2 className="mx-auto h-12 w-12 text-red-500" />
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Are you sure?
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  This action cannot be undone.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Delete
                </button>
                <button
                  onClick={cancelDelete}
                  className="mt-3 px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
